import {
  CalculatorOutlined,
  QuestionOutlined,
  SendOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { type RichiestaModuloDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaModuloDTO.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from '#theme';
import { SubmissionBurdenLogic } from '../../burden/SubmissionBurdenLogic.ts';
import {
  type ISubmissionMenuItem,
  SubmissionMenuItemType,
} from '../menu/ISubmissionMenuItem.tsx';
import { SubmissionLogic } from '../SubmissionLogic.ts';
import { type IGetSubmissionMenuLink } from './getSubmissionMenuItemLink.ts';
import { getSubmissionModuleMenuItems } from './getSubmissionModuleMenuItems.tsx';
import { SubmissionMenuKey } from './SubmissionMenuKey.ts';

interface UseSubmissionMenuItemsOptions {
  modules?: RichiestaModuloDTO[] | null;
  submission?: RichiestaDTO | null;
  submissionId?: string | null;
  getMenuLink: IGetSubmissionMenuLink;
  hideGuide?: boolean;
  hideReport?: boolean;
  hidePayment?: boolean;
  /**
   * Se `true`, indica che l'utente può inviare la richiesta.
   * Mostriamo quindi il testo "Riepilogo e Invio" anziché solo "Riepilogo".
   */
  canSubmit?: boolean;
}

export function useSubmissionMenuItems(options: UseSubmissionMenuItemsOptions) {
  const {
    modules,
    submissionId,
    submission,
    getMenuLink,
    hideGuide = false,
    hidePayment = false,
    hideReport = false,
    canSubmit,
  } = options;
  const { t } = useTranslation();

  const menuItems = useMemo(() => {
    if (!submissionId || !modules) return [];

    // Se la richiesta è in stato "inviata" non è possibile modificare i moduli,
    // quindi mostriamo un menu di sola lettura.
    // Utilizziamo solo `stato` per evitare di appesantire le dipendenze
    // dell'hook.
    const isSent = SubmissionLogic.isSubmissionSent({
      stato: submission?.stato,
    });

    const items: ISubmissionMenuItem[] = [];

    if (!isSent && !hideGuide) {
      items.push({
        key: SubmissionMenuKey.Guide,
        index: SubmissionMenuKey.Guide,
        type: SubmissionMenuItemType.Guide,
        name: t(
          'menu-items.guida-alla-compilazione',
          'Guida alla compilazione',
        ),
        icon: <QuestionOutlined aria-hidden />,
        to: getMenuLink({ submissionId, type: 'guide' }),
        // children: [],
      });
    }

    items.push(
      ...getSubmissionModuleMenuItems({
        getMenuLink,
        isSent,
        modules,
        submissionId,
      }),
    );

    if (
      SubmissionBurdenLogic.showPayments(
        submission?.statoOneri,
        submission?.statoPreventivi,
        hidePayment,
      )
    ) {
      items.push({
        key: SubmissionMenuKey.Payment,
        index: SubmissionMenuKey.Payment,
        name: t('menu-items.pagamenti', 'Pagamenti'),
        type: SubmissionMenuItemType.Payment,
        icon: <CalculatorOutlined aria-hidden />,
        to: getMenuLink({ submissionId, type: 'payment', options: { isSent } }),
      });
    }

    if (!isSent && !hideReport) {
      items.push({
        key: SubmissionMenuKey.Report,
        index: SubmissionMenuKey.Report,
        name: canSubmit
          ? t('menu-items.riepilogo-e-invio', 'Riepilogo e Invio')
          : t('menu-items.riepilogo', 'Riepilogo'),
        type: SubmissionMenuItemType.Send,
        icon: theme.showAlternateSummaryIcon ? (
          <UnorderedListOutlined aria-hidden />
        ) : (
          <SendOutlined aria-hidden />
        ),
        to: getMenuLink({ submissionId, type: 'report' }),
      });
    }

    return items;
  }, [
    submissionId,
    modules,
    submission?.stato,
    submission?.statoOneri,
    submission?.statoPreventivi,
    hideGuide,
    getMenuLink,
    hidePayment,
    hideReport,
    t,
    canSubmit,
  ]);

  // TODO - Rinominare
  // Anche se sono chiamati steps sono in realtà le chiavi dei menu items
  const steps = useMemo(() => menuItems.map(item => item.key), [menuItems]);

  return { menuItems, steps };
}
